import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { Modal, Alert, Card, Button, Row, Col, Spinner } from 'react-bootstrap';
import DepartmentTree from '../../components/admin/DepartmentTree';
import DepartmentForm from '../../components/admin/DepartmentForm';
import { getOrganizations, createDepartment, updateDepartment, deleteDepartment } from './../../api';

function AdminDepartment() {
    const title = 'Department Management';
    const [organization, setOrganization] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [alert, setAlert] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingDeptId, setDeletingDeptId] = useState(null);

    useEffect(() => {
        fetchOrganization();
    }, []);


    const fetchOrganization = async () => {
        try {
            const response = await getOrganizations();
            const data = response.data;
            if (response.status !== 200) {
                throw new Error(data.message || 'Failed to fetch organization');
            }
            setOrganization(data);
            setDepartments(data.departments || []);
        } catch (error) {
            setAlert({ type: 'danger', message: error.message });
        } finally {
            setIsLoading(false);
        }
    };


    const handleAdd = () => {
        setSelectedDept(null);
        setShowForm(true);
    };

    const handleEdit = (dept) => {
        setSelectedDept(dept);
        setShowForm(true);

    };

    const handleDelete = async () => {
        try {
            // Check if department has children
            const hasChildren = departments.some(dept => dept.parent_id === deletingDeptId);
            if (hasChildren) {
                setAlert({
                    type: 'warning',
                    message: 'Cannot delete department that has child departments. Please delete child departments first.'
                });
                setShowDeleteModal(false);
                return;
            }

            const response = await deleteDepartment(organization.id, deletingDeptId);
            if (response.status === 200) {
                setAlert({
                    type: 'success',
                    message: 'Department deleted successfully'
                });
                await fetchOrganization();
            } else {
                throw new Error('Failed to delete department');
            }
            setShowDeleteModal(false);
            setDeletingDeptId(null);
        } catch (error) {
            setAlert({
                type: 'danger',
                message: error.message || 'Failed to delete department'
            });
        }
    };
    
    const handleSubmit = async (formData) => {
        try {
            // Validate code uniqueness
            const codeExists = departments.some(dept =>
                dept.code === formData.code && dept.id !== formData.id
            );
            if (codeExists) {
                setAlert({
                    type: 'warning',
                    message: 'Department code already exists. Please use a unique code.'
                });
                return;
            }

            if (formData.id) {
                const response = await updateDepartment(organization.id, formData.id, formData);
                if (response.status === 200) {
                    setAlert({
                        type: 'success',
                        message: 'Department updated successfully'
                    });
                } else {
                    throw new Error('Failed to update department');
                }
            } else {
                const response = await createDepartment(organization.id, formData);
                if (response.status === 200) {
                    setAlert({
                        type: 'success',
                        message: 'Department created successfully'
                    });
                } else {
                    throw new Error('Failed to create department');
                }
            }

            await fetchOrganization();
            setShowForm(false);
            setSelectedDept(null);
        } catch (error) {
            setAlert({
                type: 'danger',
                message: error.message || 'Failed to save department'
            });
        }
    };
    if (isLoading) {
        return <Spinner animation="border" className="d-block mx-auto" />;
    }


    return (
        <>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <div className="container-fluid mb-5 px-3">
                <div className="row g-5 pt-3">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header pt-3 bg-white">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h4 className="text-secondary">Department Management</h4>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive position-relative">
                                <Row>
                                    {/* Left Side - Tree View */}
                                    <Col md={6} className="border-end">
                                        <Card>
                                            <Card.Header>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0">Departments</h5>
                                                    <Button variant="btn btn-outline-primary" size="sm" onClick={handleAdd}>
                                                        <i className="bi bi-plus-circle me-1"></i>
                                                        Add Department
                                                    </Button>
                                                </div>
                                            </Card.Header>
                                            <Card.Body>
                                                {departments.length === 0 ? (
                                                    <p className="text-muted text-center">No departments yet</p>
                                                ) : (
                                                    <DepartmentTree
                                                        departments={departments}
                                                        onEdit={handleEdit}
                                                        onDelete={(deptId) => {
                                                            setDeletingDeptId(deptId);
                                                            setShowDeleteModal(true);
                                                        }}
                                                    />
                                                )}
                                            </Card.Body>
                                        </Card>
                                    </Col>

                                    {/* Right Side - Details/Editor */}
                                    <Col md={6}>
                                        {showForm && (
                                            <DepartmentForm
                                                department={selectedDept}
                                                departments={departments}
                                                onSubmit={handleSubmit}
                                                onCancel={() => setShowForm(false)}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </div>

                            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                                <Modal.Header closeButton>
                                    <Modal.Title>Confirm Delete</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to delete this department?
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                        Cancel
                                    </Button>
                                    <Button variant="danger" onClick={handleDelete}>
                                        Delete
                                    </Button>
                                </Modal.Footer>
                            </Modal>

                        </div>
                    </div>
                    {alert && (
                        <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible>
                            {alert.message}
                        </Alert>
                    )}
                </div >
            </div >
        </>
    );
}

export default AdminDepartment;